/**
 * @architecture-header 1
 * @layer module-ui
 * @domain settings-users
 * @responsibility Compose independently tuned desktop and mobile SET-F05 Users pane surfaces from
 *   backend-authoritative member-directory props.
 * @state-owner React transient add-member form state; parent owns server state
 * @allowed-imports React; react-router-dom navigation; shared translator; @vispo/contracts types;
 *   react-hook-form; zod
 * @forbidden direct API client; fabricated authorization; email/provider invite flow (SET-F06 owns
 *   the actual invitation surface; this button only navigates there)
 * @public-api UsersSettingsSurface
 * @side-effects invokes parent commands only; navigates to the Settings Invitations tab
 * @tests UsersSettingsSurface.test.tsx
 */

import { zodResolver } from '@hookform/resolvers/zod';
import type {
  OrganizationMember,
  OrganizationMemberDirectory,
  OrganizationMemberRole,
} from '@vispo/contracts';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { z } from 'zod';
import { VispoLoading } from '../../../shared/components/WorkspaceState';
import { useTranslator } from '../../../shared/model/use-workspace';
import './settings-users.css';

const ROLES: readonly OrganizationMemberRole[] = ['admin', 'staff', 'member'];
const EMPTY_MEMBERS: readonly OrganizationMember[] = Object.freeze([]);

export interface UsersSettingsSurfaceProps {
  canManage: boolean;
  commandError?: unknown;
  commandSucceeded?: boolean;
  directory: OrganizationMemberDirectory | null;
  errorCode?: string | null;
  loading?: boolean;
  mobile: boolean;
  onCreate?(input: { role: OrganizationMemberRole; userId: string }): void;
  onRetry?(): void;
  onUpdate?(
    member: OrganizationMember,
    input: { role?: OrganizationMemberRole; state?: 'active' | 'removed' },
  ): void;
  saving?: boolean;
}

function InviteUsersButton() {
  const t = useTranslator();
  const navigate = useNavigate();
  return (
    <div className="users-settings-pane__invite-placeholder">
      <button type="button" onClick={() => navigate('/teacher/settings/invitations')}>
        {t('邀請成員')}
      </button>
      <span>{t('以 email 邀請連結新增成員 (SET-F06)')}</span>
    </div>
  );
}

function AddMemberForm({
  onCreate,
  saving,
}: Pick<UsersSettingsSurfaceProps, 'onCreate' | 'saving'>) {
  const t = useTranslator();
  const form = useForm<{ role: 'admin' | 'staff' | 'member'; userId: string }>({
    resolver: zodResolver(
      z.object({ userId: z.string().trim().min(1), role: z.enum(['admin', 'staff', 'member']) }),
    ),
    defaultValues: { userId: '', role: 'member' },
  });
  return (
    <form
      className="users-settings-pane__add-form"
      onSubmit={form.handleSubmit((value) => {
        onCreate?.(value);
        form.reset();
      })}
    >
      <label>
        <span>{t('使用者 ID')}</span>
        <input aria-label={t('使用者 ID')} {...form.register('userId')} />
      </label>
      <label>
        <span>{t('角色')}</span>
        <select aria-label={t('新成員角色')} {...form.register('role')}>
          {ROLES.map((value) => (
            <option key={value} value={value}>
              {value}
            </option>
          ))}
        </select>
      </label>
      <button className="bt pri" disabled={saving || !form.watch('userId').trim()} type="submit">
        {t('新增成員')}
      </button>
    </form>
  );
}

function DesktopMemberRows({
  canManage,
  members,
  onUpdate,
  saving,
}: Pick<UsersSettingsSurfaceProps, 'canManage' | 'onUpdate' | 'saving'> & {
  members: readonly OrganizationMember[];
}) {
  const t = useTranslator();
  return (
    <ul className="users-settings-pane__list">
      {members.map((member) => (
        <li className="users-settings-pane__row" key={member.membershipId}>
          <div className="users-settings-pane__identity">
            <strong>{member.displayName || t('未命名使用者')}</strong>
            <code>{member.userId}</code>
          </div>
          <span
            className={`users-settings-pane__badge users-settings-pane__badge--${member.state}`}
          >
            {member.state}
          </span>
          <label>
            <span className="sr-only">{t('角色')}</span>
            <select
              aria-label={`${member.displayName} ${t('角色')}`}
              defaultValue={member.role}
              disabled={
                !canManage || saving || member.state !== 'active' || member.role === 'owner'
              }
              onChange={(event) =>
                onUpdate?.(member, { role: event.target.value as OrganizationMemberRole })
              }
            >
              <option value="owner">owner</option>
              {ROLES.map((role) => (
                <option key={role} value={role}>
                  {role}
                </option>
              ))}
            </select>
          </label>
          {canManage && member.role !== 'owner' ? (
            <button
              className="bt sm"
              disabled={saving}
              type="button"
              onClick={() =>
                onUpdate?.(member, { state: member.state === 'removed' ? 'active' : 'removed' })
              }
            >
              {member.state === 'removed' ? t('啟用') : t('移除')}
            </button>
          ) : null}
        </li>
      ))}
    </ul>
  );
}

function MobileMemberRows({
  canManage,
  members,
  onUpdate,
  saving,
}: Pick<UsersSettingsSurfaceProps, 'canManage' | 'onUpdate' | 'saving'> & {
  members: readonly OrganizationMember[];
}) {
  const t = useTranslator();
  return (
    <div className="users-settings-pane__mobile-list">
      {members.map((member) => (
        <article className="users-settings-pane__mobile-row" key={member.membershipId}>
          <strong>{member.displayName || t('未命名使用者')}</strong>
          <span
            className={`users-settings-pane__badge users-settings-pane__badge--${member.state}`}
          >
            {member.state}
          </span>
          {canManage && member.role !== 'owner' ? (
            <button
              className="bt sm"
              disabled={saving}
              type="button"
              onClick={() =>
                onUpdate?.(member, { state: member.state === 'removed' ? 'active' : 'removed' })
              }
            >
              {member.state === 'removed' ? t('啟用') : t('移除')}
            </button>
          ) : null}
          <label>
            <span>{t('角色')}</span>
            <select
              aria-label={`${member.displayName} ${t('角色')}`}
              defaultValue={member.role}
              disabled={
                !canManage || saving || member.state !== 'active' || member.role === 'owner'
              }
              onChange={(event) =>
                onUpdate?.(member, { role: event.target.value as OrganizationMemberRole })
              }
            >
              <option value="owner">owner</option>
              {ROLES.map((role) => (
                <option key={role} value={role}>
                  {role}
                </option>
              ))}
            </select>
          </label>
        </article>
      ))}
    </div>
  );
}

export function UsersSettingsSurface(props: UsersSettingsSurfaceProps) {
  const t = useTranslator();

  if (props.loading) {
    return (
      <section
        aria-label={t('Users')}
        className={`users-settings-pane${props.mobile ? ' users-settings-pane--mobile' : ' users-settings-pane--desktop'}`}
      >
        <VispoLoading className="users-settings-pane__loading" label={t('正在載入組織成員')} />
      </section>
    );
  }

  if (props.errorCode === 'ORGANIZATION_MEMBER_ADMIN_REQUIRED') {
    return (
      <section
        aria-label={t('Users')}
        className={`users-settings-pane${props.mobile ? ' users-settings-pane--mobile' : ' users-settings-pane--desktop'}`}
      >
        <div className="users-settings-pane__blocked" role="alert">
          <strong>{t('無法查看組織成員')}</strong>
          <span>{t('只有此組織的 owner 或 admin 可以管理成員。')}</span>
        </div>
      </section>
    );
  }

  if (props.errorCode && !props.commandError) {
    return (
      <section
        aria-label={t('Users')}
        className={`users-settings-pane${props.mobile ? ' users-settings-pane--mobile' : ' users-settings-pane--desktop'}`}
      >
        <div className="users-settings-pane__blocked" role="alert">
          <strong>{t('組織成員暫時無法載入')}</strong>
          <button className="bt sm" type="button" onClick={props.onRetry}>
            {t('重試')}
          </button>
        </div>
      </section>
    );
  }

  const directory = props.directory;
  const members = directory?.members ?? EMPTY_MEMBERS;
  const Rows = props.mobile ? MobileMemberRows : DesktopMemberRows;

  return (
    <section
      aria-label={t('Users')}
      className={`users-settings-pane${props.mobile ? ' users-settings-pane--mobile' : ' users-settings-pane--desktop'}`}
    >
      {props.saving ? <div role="status">{t('正在儲存變更…')}</div> : null}
      {props.commandSucceeded ? <div role="status">{t('變更已儲存。')}</div> : null}
      {props.commandError ? (
        <div role="alert">
          {props.errorCode === 'ORGANIZATION_REVISION_CONFLICT'
            ? t('資料已更新，請重新整理後再試。')
            : props.errorCode === 'ORGANIZATION_MEMBER_DUPLICATE'
              ? t('此使用者已經是組織成員。')
              : t('變更失敗，請重新整理後再試。')}
        </div>
      ) : null}

      <div className="users-settings-pane__toolbar">
        <span>
          {members.length ? `${t('共')} ${members.length} ${t('位成員')}` : t('目前沒有成員')}
        </span>
        <span>revision {directory?.revision ?? '—'}</span>
      </div>

      {props.canManage ? (
        <>
          <InviteUsersButton />
          <AddMemberForm onCreate={props.onCreate} saving={props.saving} />
        </>
      ) : null}

      {members.length ? (
        <Rows
          canManage={props.canManage}
          members={members}
          onUpdate={props.onUpdate}
          saving={props.saving}
        />
      ) : (
        <div className="users-settings-pane__empty">{t('尚未建立組織成員。')}</div>
      )}

      {props.mobile ? (
        <p className="users-settings-pane__note">
          {t('成員會立即加入組織；此流程不會寄送邀請信。')}
        </p>
      ) : null}
    </section>
  );
}
